import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
// 'ui.grid.edit' and 'ui.grid.selection' modules are imported with uiGrid when it runs;

//! used to be: angular.module('books.ui.csmGrid', [uiGrid, 'ui.grid.edit', 'ui.grid.cellNav', 'CSMCommonWeb'])
export default angular
  .module('books.ui.csmGrid', ['ui.grid', csmCommonWeb])
  .directive('csmGrid', csmGridDirective).name;

csmGridDirective.$inject = [];

/*******************************************************************************
 * csmGrid */
/**
 * This is a wrapper for the uiGrid component that will utilize a dynamically
 * added blank row at the bottom of the data.
 *
 * When building the column definitions, if there is a property named hideNew a
 * class will be added or removed based on this flag. This class will set the
 * display to none.
 *
 * When the last cell of a row has been exited while autorow is true, validateRow
 * will be called to determine if a new row should be added, or the current row
 * be removed. If the quickRow option is set, validateRow will be called after
 * any cell has been exited.
 *
 * @ngdoc     directive
 * @memberof  books.ui.csmGrid
 * @name      books.ui.csmGrid.directive:csmGrid
 *
 * @scope
 * @property  autorow       {expression} Evaluates to a truthy expression to
 *                                       indicate that the dummy row should be
 *                                       used.
 * @property  gridAttrs     {string}     Passthrough for uiGrid attributes.
 * @property  newRowItem    {expression} Instanced object to use in the dummy row
 *                                       as the empty object.
 * @property  options       {object}     uiGrid options containing the grid flags
 *                                       and the column definitions.
 * @property  quickRow      {expression} Evaluated when a cell has been edited.
 *                                       If true, any cell edit where the new
 *                                       value is different than the old value, a
 *                                       new row will be created immediately.
 * @property  removeEmpty   {expression} Evaluated when a cell has been edited.
 *                                       If true, all data will be checked. If
 *                                       all has been cleared, remove the row. If
 *                                       this is the last row, replace with the
 *                                       dummy row.
 * @property  validateRow   {expression} Evaluated when the autorow wants to make
 *                                       a new row. A truthy response will result
 *                                       in the new row being created using the
 *                                       object from newRowItem, while a falsey
 *                                       statement will result in the removal of
 *                                       the current row from the data list.
 *******************************************************************************/
/* @ngInject */ function csmGridDirective() {
  /*----------------------------------------*/
  /* Add style rule to ensure block display */
  /* so border styling will look correct    */
  /*----------------------------------------*/
  var styleElem = angular.element(
    '\
    <style>\
      csm-grid { display: block }\
      .csm-grid-col-hide { display:none }\
    </style>'
  );
  angular.element(document.querySelector('body')).append(styleElem);

  /*****************************************************************************
   * Controller
   *****************************************************************************/
  var mGridController = function (
    $attrs,
    $compile,
    $element,
    $q,
    $scope,
    $timeout,
    CommonUIGridTemplates,
    uiGridConstants
  ) {
    /***************************************************************************
     * Constants
     ***************************************************************************/
    var CLASS_COL_HIDE = 'csm-grid-col-hide';
    var CLASS_GRID_CELL_CONTENTS = 'ui-grid-cell-contents';

    /***************************************************************************
     * Properties
     ***************************************************************************/
    /** Controller instance. */ var mCtrl = this;
    /** User defined onRegister function. */ var mReg =
      $scope.options.onRegisterApi;
    /** Quick Row flag. */ var mQuickRow = $scope.quickRow();
    /** ngModel Controller Reference. */ var mNgModelCtrl = null;
    /** Dummy row reference. */ var mDummyRow = null;

    /** Div to hold the uiGrid. */
    var div =
      '<div id="grid" ui-grid="options" ' +
      $scope.gridAttrs +
      ($scope.autorow() ? ' ui-grid-edit ui-grid-cellNav' : ' ') +
      ' class="grid"></div>';
    $element.append($compile(div)($scope));

    /***************************************************************************
     * addNewRow */
    /**
     * Adds a new row to the data list. This will also check for the hideNew
     * property on column definitions for the the new object to hide the contents
     * if set to true.
     ***************************************************************************/
    function addNewRow() {
      /*------------------------------------------------*/
      /* Clear the csm-grid-col-hide class from all     */
      /* elements with the ui-grid-cell-contents class. */
      /*------------------------------------------------*/
      angular
        .element($element[0].querySelectorAll('.' + CLASS_GRID_CELL_CONTENTS))
        .removeClass(CLASS_COL_HIDE);

      /*--------------------------------------------*/
      /* Only continue if using the autorow option. */
      /*--------------------------------------------*/
      if (!$scope.autorow()) return;

      mDummyRow = $scope.newRowItem();
      $scope.options.data.push(mDummyRow);

      /*----------------------------------------------*/
      /* Wait for the grid to refresh the DOM so that */
      /* we have the new row available to manipulate. */
      /*----------------------------------------------*/
      $timeout(function () {
        /*----------------------------------------------*/
        /* Find the input cells of the last row by      */
        /* looking for the ui-grid-cell-contents class. */
        /*----------------------------------------------*/
        var inputRow = $element[0].querySelectorAll(
          '.ui-grid-canvas .ui-grid-row'
        )[$scope.options.data.length - 1];
        var inputCells = inputRow.querySelectorAll(
          '.' + CLASS_GRID_CELL_CONTENTS
        );

        /*---------------------------------------------------*/
        /* If the columnDef has the hideNew flag set to      */
        /* true, then add the csm-grid-col-hide class to the */
        /* new row's cell contents element for that column.  */
        /*---------------------------------------------------*/
        for (var i = 0; i < inputCells.length; i++) {
          var colDef = $scope.options.columnDefs[i];
          var cell = inputCells[i];

          if (colDef && colDef.hideNew)
            angular.element(cell).addClass(CLASS_COL_HIDE);
        }
      });
    }

    /***************************************************************************
     * afterCellEdit */
    /**
     * Executes after the cell has been edited. If this was not fired for the
     * last row, we can only check if the row should be removed.
     *
     * For all other cases, we must be looking at the last row, and will check
     * if the newValue is different than the oldValue (for quickRow only), or the
     * last cell was edited. If either case is true, validateRow is used to
     * determine of a new row will be added.
     *
     * @param  rowEntity  Current row information.
     * @param  colDef     Current column information.
     * @param  newValue   New value entered by user.
     * @param  oldValue   Old value of cell.
     ***************************************************************************/
    function afterCellEdit(rowEntity, colDef, newValue, oldValue) {
      if (!$scope.autorow()) return;

      var lastRow =
        $scope.options.data[
          $scope.options.api.core.getVisibleRows().length - 1
        ];

      /*--------------------------------------------------------------------------*/
      /* Check validateRow. If the response is true, this is an indication that a */
      /* new row may be created. A false indicates that the current row should be */
      /* removed. If there was no response, or the response is null, do nothing.  */
      /*--------------------------------------------------------------------------*/
      $q.when($scope.validateRow({ $data: rowEntity })).then(function (resp) {
        if (resp == undefined || resp == null) return;

        /*------------------------------------------------------------*/
        /* If the response is false, remove the current row. If this  */
        /* row happens to be the last row, add a new replacement row. */
        /*------------------------------------------------------------*/
        if (!resp) {
          var index = $scope.options.data.indexOf(rowEntity);
          $scope.options.data.splice(index, 1);

          if (rowEntity == lastRow) addNewRow();
        } else if (rowEntity == lastRow) {
          /*--------------------------------------------------------*/
          /* Otherwise, if the response is truthy, we can add a new */
          /* row. We only want to do this for the last row however. */
          /*--------------------------------------------------------*/
          /*----------------------------------------------*/
          /* If the quickRow option has been set, we      */
          /* only need to check if the value has changed. */
          /*----------------------------------------------*/
          if (mQuickRow && newValue != oldValue) addNewRow();
          /*-------------------------------------*/
          /* Otherwise, make sure this was the   */
          /* last editable cell of the last row. */
          /*-------------------------------------*/ else if (
            colDef == lastEditableColumnDef()
          ) {
            addNewRow();

            /*---------------------------------------------------------------*/
            /* On next digest cycle, scroll to last row and put focus on the */
            /* first cell. We don't want to do this for the quickRow because */
            /* the user may still want to enter data for that row first.     */
            /*---------------------------------------------------------------*/
            $timeout(function () {
              if ($scope.options.api) {
                var row =
                  $scope.options.data[
                    $scope.options.api.core.getVisibleRows().length - 1
                  ];
                var col = $scope.options.columnDefs[0];
                $scope.options.api.cellNav.scrollToFocus(row, col);
              }
            });
          }
        }
      });
    }

    /***************************************************************************
     * init */
    /**
     * Initialization function to be called by the link method of the directive
     * so the ngModel Controller reference can be passed.
     *
     * @param  ngModelCtrl  ngModel controller object.
     ***************************************************************************/
    mCtrl.init = function (ngModelCtrl) {
      mNgModelCtrl = ngModelCtrl;
      mNgModelCtrl.$modelValue = mNgModelCtrl.$modelValue || [];

      /*--------------------------------------------------*/
      /* Add watch expression for the autorow option. If  */
      /* changed, we need to add or remove the dummy row. */
      /*--------------------------------------------------*/
      $scope.$watch($scope.autorow, function (newValue, oldValue) {
        /*----------------------------------------------*/
        /* When switching from using the autorow to     */
        /* not using the autorow, remove the dummy row. */
        /*----------------------------------------------*/
        if (oldValue && !newValue)
          $scope.options.data.splice($scope.options.data.length - 1, 1);
        /*-------------------------------------------*/
        /* When switching from not using the autorow */
        /* to using the autorow, add the dummy row.  */
        /*-------------------------------------------*/ else if (
          newValue &&
          !oldValue
        )
          addNewRow();
      });

      /*-------------------------------------------------*/
      /* Add watch expression for the ngModel data list. */
      /*-------------------------------------------------*/
      $scope.$watchCollection(
        function () {
          return mNgModelCtrl.$modelValue;
        },
        function (newData, oldData) {
          watchModelHandler(newData, oldData, true);
        }
      );

      /*---------------------------------------------------------*/
      /* Add watch expression for the internal uiGrid data list. */
      /*---------------------------------------------------------*/
      $scope.$watchCollection('options.data', function (newData, oldData) {
        watchModelHandler(newData, oldData, false);
      });
    };

    /***************************************************************************
     * lastEditableColumnDef */
    /**
     * Finds the last editable column definition. Loop through all of the
     * columns, setting the local variable each time it is seen as editable. This
     * will result in only returning the last one.
     *
     * @return  {Object}  Column definition object.
     ***************************************************************************/
    function lastEditableColumnDef() {
      var colDef = null;

      $scope.options.columnDefs.forEach(function (cd) {
        if (cd.enableCellEdit) colDef = cd;
      });

      return colDef;
    }

    /***************************************************************************
     * register */
    /**
     * Redefines the onRegisterApi of the options. If there was already one set,
     * call the original function first.
     *
     * @param  api  Grid Api.
     ***************************************************************************/
    function register(api) {
      if (mReg) mReg.call(this, api);

      /*--------------------------------------*/
      /* If the autorow is requested,         */
      /* register the afterCellEdit function. */
      /*--------------------------------------*/
      $scope.options.api.edit.on.afterCellEdit($scope, afterCellEdit);
    }

    /***************************************************************************
     * watchModelHandler */
    /**
     * This is the handler for the two watches set up to synchronize the data
     * between the ngModel and the internal uiGrid data. When synchronizing, the
     * ngModel will not have the dummy row, but the uiGrid data will. If the
     * dummy row is being removed directly, make sure to create another dummy row
     * to replace it. This will create an array with a new reference in either
     * case.
     *
     * @param  newData  New data from the $watch.
     * @param  oldData  Old data from the $watch.
     * @param  toGrid   Flag to determine where the data is going.
     *                  true=To the internal ui-grid.
     *                  false=To the ngModel.
     ***************************************************************************/
    function watchModelHandler(newData, oldData, toGrid) {
      if (newData == oldData) return;

      /*--------------------------------------------------*/
      /* Make a copy of the array so that we don't remove */
      /* or add rows to the originally referenced list.   */
      /*--------------------------------------------------*/
      var arr = newData.concat();

      /*-------------------------------------------------------------*/
      /* If the autorow option is in use, we will need to add or     */
      /* remove the dummy row, depending on where the data is going. */
      /*-------------------------------------------------------------*/
      if ($scope.autorow() && !toGrid) {
        var index = arr.length - 1;

        /*-----------------------------------------------*/
        /* Make sure we are only removing the dummy row. */
        /*-----------------------------------------------*/
        if (arr[index] == mDummyRow) arr.splice(index, 1);
        /*-------------------------------------------------*/
        /* If this is the last index, and it wasn't the    */
        /* dummy row, then the dummy row is being          */
        /* deleted. Add the dummy back to the uiGrid list. */
        /*-------------------------------------------------*/ else addNewRow();
      }

      /*-----------------------------------------------------*/
      /* If this is coming from the ngModel, we will need to */
      /* add the dummy row if the autorow option is in use.  */
      /*-----------------------------------------------------*/
      if (toGrid) {
        /*------------------------------------------------*/
        /* Clear the csm-grid-col-hide class from all     */
        /* elements with the ui-grid-cell-contents class. */
        /*------------------------------------------------*/
        angular
          .element($element[0].querySelectorAll('.' + CLASS_GRID_CELL_CONTENTS))
          .removeClass(CLASS_COL_HIDE);
        $scope.options.data = arr;

        if ($scope.autorow()) addNewRow();

        $timeout(function () {
          $scope.$apply();
        });
      } else {
        /*--------------------------------*/
        /* Otherwise, update the ngModel. */
        /*--------------------------------*/
        mNgModelCtrl.$modelValue.length = 0;
        arr.forEach(function (item) {
          mNgModelCtrl.$modelValue.push(item);
        });
        //        mNgModelCtrl.$setViewValue(arr);
      }
    }

    /*---------------------------------------------------------*/
    /* If the autorow option is being used, add the dummy row. */
    /*---------------------------------------------------------*/
    if ($scope.autorow()) {
      if (($scope.options.data || []).length == 0) $scope.options.data = [];

      addNewRow();
    }

    $scope.options.onRegisterApi = register;
  };

  /*****************************************************************************
   * Directive Object
   *****************************************************************************/
  return {
    restrict: 'E',
    require: ['ngModel', 'csmGrid'],
    scope: {
      autorow: '&',
      gridAttrs: '@',
      newRowItem: '&',
      options: '=',
      removeEmpty: '&',
      quickRow: '&',
      validateRow: '&' // Provided $data
    },
    link: function (element, scope, attr, reqCtrls) {
      reqCtrls[1].init(reqCtrls[0]);
    },
    controller: mGridController
  };
}
